import React, { useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { AppSettings } from '../../config/app-settings.js';
import { 
  useLogin,
  useAppSettings, 
} from '../../hooks/useAuth.js';
import useAuthStore from '../../stores/auth-store.js';

function Login() {
  const context = useContext(AppSettings);
  const navigate = useNavigate();
  const hasNavigated = useRef(false); // Track if we've already navigated
  
  const loginMutation = useLogin();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const landingPage = useAuthStore((state) => state.landingPage);
  const error = useAuthStore((state) => state.error);
  const clearError = useAuthStore((state) => state.clearError);
  const clearRedirect = useAuthStore((state) => state.clearRedirect);
  
  // Fetch auth settings (enabled only when authenticated)
  // Get React Query states for loading and error handling
  const { isLoading: settingsLoading, isError: settingsError } = useAppSettings('auth', { 
    enabled: isAuthenticated 
  });
  
  const { register, handleSubmit, formState: { errors } } = useForm();

  useEffect(() => {
    context.handleSetAppSidebarNone(true);
    context.handleSetAppHeaderNone(true);
    context.handleSetAppContentClass('p-0');

    return () => {
      context.handleSetAppSidebarNone(false);
      context.handleSetAppHeaderNone(false);
      context.handleSetAppContentClass('');
      clearError();
    };
  }, [context, clearError]);

  // Handle navigation after successful login AND settings load
  useEffect(() => {
    // Only proceed if authenticated and settings finished loading
    if (isAuthenticated && !settingsLoading && !hasNavigated.current) {
      
      // Check for settings error
      if (settingsError) {
        hasNavigated.current = true;
        Swal.fire({
          icon: 'error',
          title: 'Settings Error',
          text: 'Failed to load application settings. Please contact support.',
          confirmButtonText: 'OK',
        });
        return;
      }
      
      // Check if landing page is available
      if (landingPage) {
        hasNavigated.current = true;
        clearRedirect();
        navigate(landingPage, { replace: true });
      } else {
        // Settings loaded but no landing page found
        hasNavigated.current = true;
        Swal.fire({
          icon: 'warning',
          title: 'Settings Not Found',
          text: 'Could not load landing page settings from database. Please contact support.',
          confirmButtonText: 'OK',
        });
      }
    }
  }, [isAuthenticated, settingsLoading, settingsError, landingPage, navigate, clearRedirect]);

  const onSubmit = (data) => {
    hasNavigated.current = false; // Reset navigation flag before login
    loginMutation.mutate({
      username: data.username,
      password: data.password,
    });
  };

  return (
    <div className="login login-with-news-feed">
      <div className="news-feed">
        <div className="news-image" style={{ backgroundImage: 'url(/assets/img/login-bg/login-background.jpg)' }}></div>
        <div className="news-caption">
          <h4 className="caption-title"><b>Traffica</b></h4>
          <p>Powered by Acuere</p>
        </div>
      </div>

      <div className="login-container">
        <div className="login-header mb-30px">
          <div className="brand">
            <div className="d-flex align-items-center">
              <b>Traffica</b>
            </div>
            <small>Please use your credentials to login.</small>
          </div>
          <div className="icon">
            <i className="fa fa-sign-in-alt"></i>
          </div>
        </div>

        <div className="login-content">
          {error && (
            <div className="alert alert-danger">
              <i className="fa fa-exclamation-circle me-2"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="fs-13px">
            <div className="form-floating mb-15px">
              <input
                type="text"
                className={`form-control h-45px fs-13px ${errors.username ? 'is-invalid' : ''}`}
                placeholder="Username"
                id="username"
                {...register("username", {
                  required: "Username is required",
                  minLength: { value: 4, message: "Username must be at least 4 characters" }
                })}
              />
              <label htmlFor="username" className="d-flex align-items-center fs-13px text-gray-600">
                Username
              </label>
              {errors.username && (
                <div className="invalid-feedback">{errors.username.message}</div>
              )}
            </div>

            <div className="form-floating mb-15px">
              <input
                type="password"
                className={`form-control h-45px fs-13px ${errors.password ? 'is-invalid' : ''}`}
                placeholder="Password"
                id="password"
                {...register("password", {
                  required: "Password is required",
                  minLength: { value: 6, message: "Password must be at least 6 characters" }
                })}
              />
              <label htmlFor="password" className="d-flex align-items-center fs-13px text-gray-600">
                Password
              </label>
              {errors.password && (
                <div className="invalid-feedback">{errors.password.message}</div>
              )}
            </div>

            <div className="form-check mb-30px">
              <input
                className="form-check-input"
                type="checkbox"
                id="rememberMe"
                {...register("remember")}
              />
              <label className="form-check-label" htmlFor="rememberMe">
                Remember Me
              </label>
            </div>

            <div className="mb-15px">
              <button
                type="submit"
                className="btn btn-theme d-block h-45px w-100 btn-lg fs-14px"
                disabled={loginMutation.isPending}
              >
                {loginMutation.isPending ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </div>

            <hr className="bg-gray-600 opacity-2" />
            <div className="text-gray-600 text-center text-gray-500-darker mb-0">
              &copy; 2024-{new Date().getFullYear()} Acuere Consulting Inc. All rights reserved.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;